import { useAudioUpload } from '@/hooks/use-audio-upload';
import { useHomeUploadStore } from '@/stores/home-upload';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { Id } from '~/convex/_generated/dataModel';

export interface AudioFilePickerRef {
  openFilePicker: () => void;
}

interface AudioFilePickerProps {
  templateId?: Id<'templates'> | null;
  onUploadStart?: () => void;
  onUploadComplete: (reportId: Id<'reports'>) => void;
  onError: (message: string) => void;
  disabled?: boolean;
}

export const AudioFilePicker = forwardRef<
  AudioFilePickerRef,
  AudioFilePickerProps
>(({ templateId, onUploadStart, onUploadComplete, onError, disabled }, ref) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isUploading = useHomeUploadStore((s) => s.isUploading);

  const { uploadFile } = useAudioUpload({
    templateId,
    onUploadStart,
    onUploadComplete,
    onError,
  });

  useImperativeHandle(ref, () => ({
    openFilePicker: () => {
      if (!disabled && !isUploading) {
        fileInputRef.current?.click();
      }
    },
  }));

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = '';
    await uploadFile(file);
  };

  return (
    <input
      ref={fileInputRef}
      type="file"
      accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/m4a,audio/aac,audio/ogg,audio/flac,audio/x-flac,audio/aiff,audio/opus,audio/webm,.mp3,.wav,.m4a,.aac,.ogg,.flac,.aiff,.opus,.webm,.ds2,.dss"
      onChange={handleFileSelect}
      className="hidden"
      disabled={disabled || isUploading}
    />
  );
});

AudioFilePicker.displayName = 'AudioFilePicker';
